/* Recipe card template: editor, live preview and print, all in the browser.
   Markup: sections/rk-recipe-card-template.liquid. Card designs: assets/rk-recipe-cards.css.

   window.RKRecipeCards is the render API. The page uses it, and so does the PDF pack
   build (scripts/recipe-card-pack/build.mjs), so the pack and the tool always match.
   Nothing typed or uploaded here leaves the browser. The recipe text is kept in
   localStorage so a reload does not lose it; the photo is not stored. */
(function () {
  'use strict';

  var DESIGNS = [
    { id: 'hero', label: 'Hero', desc: 'Photo across the top, title over it' },
    { id: 'editorial', label: 'Editorial', desc: 'Magazine style, serif title' },
    { id: 'classic', label: 'Classic', desc: 'Banner photo, centred title' },
    { id: 'compact', label: 'Compact', desc: 'Dense, small photo' },
    { id: 'card-frame', label: 'Card', desc: 'Framed in your colour' },
    { id: 'index', label: 'Recipe box', desc: 'Ruled index card' },
  ];

  var SIZES = {
    '4x6': { label: '4 × 6 in card', card: true, w: '6in', h: '4in', perSheet: 2 },
    '3x5': { label: '3 × 5 in card', card: true, w: '5in', h: '3in', perSheet: 3 },
    letter: { label: 'Letter page', card: false, w: '8.5in', h: '11in' },
    a4: { label: 'A4 page', card: false, w: '210mm', h: '297mm' },
  };

  var PAPER = {
    letter: { size: '8.5in 11in', cls: 'rkrc-sheet--letter' },
    a4: { size: '210mm 297mm', cls: 'rkrc-sheet--a4' },
  };

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* One item per non-empty line. A line ending in ":" is a group heading ("For the sauce:").
     Leading list markers ("1.", "2)", "-", "•") are dropped: the card numbers steps itself. */
  function lines(text) {
    return String(text || '')
      .split(/\r?\n/)
      .map(function (l) {
        return l.replace(/^\s*(?:\d+[.)]|[-*•])\s+/, '').trim();
      })
      .filter(Boolean)
      .map(function (l) {
        var group = /:$/.test(l) && l.length <= 60;
        return { text: group ? l.slice(0, -1) : l, group: group };
      });
  }

  function parse(f) {
    f = f || {};
    return {
      title: (f.title || '').trim(),
      category: (f.category || '').trim(),
      author: (f.author || '').trim(),
      description: (f.description || '').trim(),
      servings: (f.servings || '').trim(),
      prep: (f.prep || '').trim(),
      cook: (f.cook || '').trim(),
      ingredients: lines(f.ingredients),
      steps: lines(f.steps),
      notes: (f.notes || '').trim(),
      photo: f.photo || '',
    };
  }

  /* Ruled lines as real elements: Chrome drops hairlines drawn with repeating gradients
     when it prints. The container clips whatever does not fit. */
  function rules(extra) {
    return '<span class="rkrc__rules' + (extra ? ' ' + extra : '') + '" aria-hidden="true">' + new Array(61).join('<i></i>') + '</span>';
  }

  function listItems(items) {
    return items
      .map(function (it) {
        return it.group ? '<li class="rkrc__group">' + esc(it.text) + '</li>' : '<li>' + esc(it.text) + '</li>';
      })
      .join('');
  }

  function stepCount(items) {
    return items.filter(function (it) {
      return !it.group;
    }).length;
  }

  /* Build one card as an HTML string.
     part (optional) limits the lists for pagination:
       { cont: bool, ing: [...], steps: [...], stepStart: n, notes: bool } */
  function cardHTML(r, o, part) {
    o = o || {};
    var design = o.design || 'hero';
    var size = SIZES[o.size] ? o.size : '4x6';
    var blank = Boolean(o.blank);
    var cont = Boolean(part && part.cont);
    var hasPhoto = !blank && !cont && Boolean(r.photo) && design !== 'index';
    var ing = part ? part.ing : r.ingredients;
    var steps = part ? part.steps : r.steps;
    var showNotes = part ? part.notes : Boolean(r.notes);
    var labels = o.labels || {};

    var cls = ['rkrc', 'rkrc--' + design, 'rkrc--' + size, SIZES[size].card ? 'rkrc--card' : 'rkrc--page'];
    if (blank) cls.push('rkrc--blank');
    if (cont) cls.push('rkrc--cont');
    if (!hasPhoto) cls.push('rkrc--nophoto');

    var style = '--rkrc-accent:' + esc(o.accent || '#eb4c2e') + ';';
    if (o.scale && o.scale !== 1) style += '--rkrc-scale:' + o.scale + ';';

    var h = '<article class="' + cls.join(' ') + '" style="' + style + '">';

    // Header
    h += '<header class="rkrc__head">';
    if (cont) {
      h += '<div class="rkrc__titles"><p class="rkrc__title">' + esc(r.title || 'Recipe') + ' <span>(continued)</span></p></div>';
    } else {
      if (hasPhoto) h += '<div class="rkrc__photo"><img src="' + esc(r.photo) + '" alt=""></div>';
      h += '<div class="rkrc__titles">';
      if (blank) {
        h += '<p class="rkrc__kicker">' + esc(labels.recipe || 'Recipe') + '</p>';
        h += '<p class="rkrc__title"><span class="rkrc__write"></span></p>';
        h += '<p class="rkrc__byline">' + esc(labels.from || 'From the kitchen of') + ' <span class="rkrc__write" style="display:inline-block;min-width:9em;min-height:1.2em"></span></p>';
      } else {
        if (r.category) h += '<p class="rkrc__kicker">' + esc(r.category) + '</p>';
        h += '<p class="rkrc__title">' + esc(r.title || 'Untitled recipe') + '</p>';
        if (r.author) h += '<p class="rkrc__byline">' + esc(labels.from || 'From the kitchen of') + ' ' + esc(r.author) + '</p>';
        if (r.description && (design === 'editorial' || design === 'compact' || design === 'card-frame')) {
          h += '<p class="rkrc__desc">' + esc(r.description) + '</p>';
        }
      }
      h += '</div>';
    }
    h += '</header>';

    // Stats and description
    if (!cont) {
      var stats = [
        ['Serves', r.servings],
        ['Prep', r.prep],
        ['Cook', r.cook],
      ];
      var shown = stats.filter(function (s) {
        return blank || s[1];
      });
      if (shown.length) {
        h += '<dl class="rkrc__meta">';
        shown.forEach(function (s) {
          h += '<div class="rkrc__stat"><dt>' + s[0] + '</dt><dd>' + (blank ? '<span class="rkrc__write"></span>' : esc(s[1])) + '</dd></div>';
        });
        h += '</dl>';
      }
      if (!blank && r.description && !(design === 'editorial' || design === 'compact' || design === 'card-frame')) {
        h += '<p class="rkrc__desc">' + esc(r.description) + '</p>';
      }
    }

    // Body
    var start = part && part.stepStart ? part.stepStart : 0;
    var index = design === 'index';
    var colLines = index ? '' : rules();
    var ingLines = index ? '' : design === 'classic' ? rules('rkrc__rules--l') + rules('rkrc__rules--r') : rules();
    h += '<div class="rkrc__body">';
    if (index) h += rules('rkrc__rules--body');
    if (blank) {
      h += '<section class="rkrc__ing"><p class="rkrc__h">Ingredients</p><div class="rkrc__lines">' + ingLines + '</div></section>';
      h += '<section class="rkrc__steps"><p class="rkrc__h">Directions</p><div class="rkrc__lines">' + colLines + '</div></section>';
    } else {
      if (!part || ing.length) {
        h += '<section class="rkrc__ing"><p class="rkrc__h">Ingredients' + (cont ? ' <span>(cont.)</span>' : '') + '</p><ul class="rkrc__list">' + listItems(ing) + '</ul></section>';
      }
      if (!part || steps.length) {
        h += '<section class="rkrc__steps"><p class="rkrc__h">Directions' + (cont && start ? ' <span>(cont.)</span>' : '') + '</p><ol class="rkrc__list" style="--rkrc-start:' + start + '">' + listItems(steps) + '</ol></section>';
      }
    }
    h += '</div>';

    if (blank) {
      h += '<section class="rkrc__notes"><p class="rkrc__h">Notes</p><div class="rkrc__lines">' + rules() + '</div></section>';
    } else if (showNotes && r.notes) {
      h += '<section class="rkrc__notes"><p class="rkrc__h">Notes</p><p>' + esc(r.notes) + '</p></section>';
    }

    h += '<div class="rkrc__end"></div>';
    if (o.credit !== false) h += '<p class="rkrc__credit">' + esc(o.creditText || 'Recipe card template from recipekit.com') + '</p>';
    h += '</article>';
    return h;
  }

  function toEl(html) {
    var t = document.createElement('template');
    t.innerHTML = html.trim();
    return t.content.firstElementChild;
  }

  function overflows(el) {
    return el.scrollHeight > el.clientHeight + 1;
  }

  var measureHost;
  function host() {
    if (measureHost && measureHost.isConnected) return measureHost;
    measureHost = document.createElement('div');
    measureHost.setAttribute('aria-hidden', 'true');
    measureHost.style.cssText = 'position:absolute;left:-20000px;top:0;visibility:hidden;pointer-events:none;';
    document.body.appendChild(measureHost);
    return measureHost;
  }

  function fits(html) {
    var h = host();
    h.innerHTML = html;
    var ok = !overflows(h.firstElementChild);
    h.innerHTML = '';
    return ok;
  }

  /* Split a recipe over as many cards as it needs.
     First try the whole recipe on one card, shrinking text a little if that is enough.
     Otherwise fill card 1, then continuation cards, item by item, measuring each time.
     Returns { cards: [html], scale, clipped }. */
  function paginate(r, o) {
    o = o || {};
    if (o.blank) return { cards: [cardHTML(r, o)], scale: 1, clipped: false };

    var scales = [1, 0.94, 0.88];
    for (var i = 0; i < scales.length; i++) {
      var single = cardHTML(r, Object.assign({}, o, { scale: scales[i] }));
      if (fits(single)) return { cards: [single], scale: scales[i], clipped: false };
    }

    var scale = 0.94;
    var opts = Object.assign({}, o, { scale: scale });
    var cards = [];
    var clipped = false;
    var stepsDone = 0;
    var ingLeft = r.ingredients.slice();
    var stepsLeft = r.steps.slice();
    var notesLeft = Boolean(r.notes);

    // Two-column cards fill both columns before moving on, so card 1 never shows an empty
    // Directions column. Stacked layouts (Classic, continuation cards) keep reading order:
    // nothing from a later list goes on a card while an earlier list is still unfinished.
    while ((ingLeft.length || stepsLeft.length || notesLeft) && cards.length < 12) {
      var cont = cards.length > 0;
      var stacked = cont || o.design === 'classic';
      var part = { cont: cont, ing: [], steps: [], stepStart: stepsDone, notes: false };
      var placed = 0;
      var blocked = false;

      while (ingLeft.length) {
        var t1 = Object.assign({}, part, { ing: part.ing.concat([ingLeft[0]]) });
        if (fits(cardHTML(r, opts, t1))) { part = t1; ingLeft.shift(); placed++; }
        else { blocked = true; break; }
      }
      if (!(stacked && blocked)) {
        while (stepsLeft.length) {
          var t2 = Object.assign({}, part, { steps: part.steps.concat([stepsLeft[0]]) });
          if (fits(cardHTML(r, opts, t2))) { part = t2; if (!stepsLeft[0].group) stepsDone++; stepsLeft.shift(); placed++; }
          else { blocked = true; break; }
        }
      }
      if (notesLeft && !ingLeft.length && !stepsLeft.length) {
        var t3 = Object.assign({}, part, { notes: true });
        if (fits(cardHTML(r, opts, t3))) { part = t3; notesLeft = false; placed++; }
      }
      if (!placed) {
        // One item bigger than an empty card: place it anyway, and say so.
        clipped = true;
        if (ingLeft.length) part.ing.push(ingLeft.shift());
        else if (stepsLeft.length) { if (!stepsLeft[0].group) stepsDone++; part.steps.push(stepsLeft.shift()); }
        else { part.notes = true; notesLeft = false; }
      }
      cards.push(cardHTML(r, opts, part));
    }
    if (ingLeft.length || stepsLeft.length || notesLeft) clipped = true;
    // A recipe with no lists at all still needs its first card.
    if (!cards.length) cards.push(cardHTML(r, opts, { cont: false, ing: [], steps: [], stepStart: 0, notes: false }));
    return { cards: cards, scale: scale, clipped: clipped };
  }

  /* Print layout: one card per page on card stock, or several per sheet with cut lines. */
  function printPages(cards, size, paper) {
    var s = SIZES[size];
    if (!s.card || paper === 'card') {
      return {
        pageSize: s.w + ' ' + s.h,
        pages: cards.map(function (c) {
          return '<div class="rct-print-page">' + c + '</div>';
        }),
      };
    }
    var p = PAPER[paper] || PAPER.letter;
    var pages = [];
    for (var i = 0; i < cards.length; i += s.perSheet) {
      var group = cards.slice(i, i + s.perSheet);
      pages.push(
        '<div class="rct-print-page"><div class="rkrc-sheet ' + p.cls + (s.perSheet > 2 ? ' rkrc-sheet--tight' : '') + '">' +
          group
            .map(function (c) {
              return '<div class="rkrc-cut">' + c + '</div>';
            })
            .join('') +
          '</div></div>'
      );
    }
    return { pageSize: p.size, pages: pages };
  }

  window.RKRecipeCards = {
    DESIGNS: DESIGNS,
    SIZES: SIZES,
    parse: parse,
    cardHTML: cardHTML,
    paginate: paginate,
    printPages: printPages,
  };

  /* ======================================================================
     Editor (only when the tool section is on the page)
     ====================================================================== */

  var STORE_KEY = 'rk-recipe-card:v1';
  var FIELDS = ['title', 'category', 'author', 'description', 'servings', 'prep', 'cook', 'ingredients', 'steps', 'notes'];

  function track(name, data) {
    try {
      if (window.umami && typeof window.umami.track === 'function') window.umami.track(name, data);
    } catch (e) {}
  }

  function initTool(root) {
    var example = {};
    try {
      example = JSON.parse(root.querySelector('[data-rct-example]').textContent);
    } catch (e) {}
    var defaultPhoto = root.dataset.examplePhoto || '';

    var state = {
      design: 'hero',
      size: '4x6',
      accent: '#eb4c2e',
      blank: false,
      paper: 'letter',
      credit: true,
      photo: defaultPhoto,
      photoIsExample: true,
    };

    var form = root.querySelector('[data-rct-form]');
    var preview = root.querySelector('[data-rct-preview]');
    var statusEl = root.querySelector('[data-rct-status]');
    var paperRow = root.querySelector('[data-rct-paper-row]');
    var photoInput = root.querySelector('[data-rct-photo]');
    var photoClear = root.querySelector('[data-rct-photo-clear]');
    var photoName = root.querySelector('[data-rct-photo-name]');
    var fieldsWrap = root.querySelector('[data-rct-fields]');
    var objectUrl = null;

    function field(name) {
      return form.elements[name];
    }

    function setFields(data) {
      FIELDS.forEach(function (k) {
        if (field(k)) field(k).value = data[k] || '';
      });
    }

    function readFields() {
      var out = {};
      FIELDS.forEach(function (k) {
        out[k] = field(k) ? field(k).value : '';
      });
      return out;
    }

    // Restore saved work, else the example.
    var saved = null;
    try {
      saved = JSON.parse(window.localStorage.getItem(STORE_KEY) || 'null');
    } catch (e) {}
    if (saved && saved.fields) {
      setFields(saved.fields);
      ['design', 'size', 'accent', 'paper'].forEach(function (k) {
        if (saved[k]) state[k] = saved[k];
      });
      if (typeof saved.blank === 'boolean') state.blank = saved.blank;
      if (typeof saved.credit === 'boolean') state.credit = saved.credit;
      if (saved.noPhoto) state.photo = '';
    } else {
      setFields(example);
    }
    if (!DESIGNS.some(function (d) { return d.id === state.design; })) state.design = 'hero';
    if (!SIZES[state.size]) state.size = '4x6';

    function save() {
      try {
        window.localStorage.setItem(
          STORE_KEY,
          JSON.stringify({
            fields: readFields(),
            design: state.design,
            size: state.size,
            accent: state.accent,
            paper: state.paper,
            blank: state.blank,
            credit: state.credit,
            noPhoto: !state.photo,
          })
        );
      } catch (e) {}
    }

    function syncControls() {
      root.querySelectorAll('[data-rct-design]').forEach(function (b) {
        b.setAttribute('aria-checked', String(b.dataset.rctDesign === state.design));
        b.tabIndex = b.dataset.rctDesign === state.design ? 0 : -1;
      });
      root.querySelectorAll('input[name="rct-size"]').forEach(function (i) {
        i.checked = i.value === state.size;
      });
      root.querySelectorAll('input[name="rct-mode"]').forEach(function (i) {
        i.checked = (i.value === 'blank') === state.blank;
      });
      root.querySelectorAll('[data-rct-swatch]').forEach(function (b) {
        b.setAttribute('aria-pressed', String(b.dataset.rctSwatch.toLowerCase() === state.accent.toLowerCase()));
      });
      var custom = root.querySelector('[data-rct-color]');
      if (custom) custom.value = state.accent;
      var paper = root.querySelector('[data-rct-paper]');
      if (paper) paper.value = state.paper;
      var credit = root.querySelector('[data-rct-credit]');
      if (credit) credit.checked = state.credit;
      paperRow.hidden = !SIZES[state.size].card;
      fieldsWrap.classList.toggle('is-blank', state.blank);
      root.querySelectorAll('[data-rct-sizename]').forEach(function (el) {
        el.textContent = SIZES[state.size].label;
      });
      photoClear.hidden = !state.photo;
      photoName.textContent = state.photo ? (state.photoIsExample ? 'Example photo' : 'Your photo') : 'No photo';
    }

    function opts() {
      return { design: state.design, size: state.size, accent: state.accent, blank: state.blank, credit: state.credit };
    }

    var last = null;

    function fitSheets() {
      var w = preview.clientWidth;
      preview.querySelectorAll('.rct-sheet').forEach(function (sheet) {
        var card = sheet.firstElementChild;
        var cw = card.offsetWidth;
        var ch = card.offsetHeight;
        var s = Math.min(1, w / cw);
        card.style.transform = 'scale(' + s + ')';
        sheet.style.width = cw * s + 'px';
        sheet.style.height = ch * s + 'px';
      });
    }

    function render() {
      var recipe = parse(Object.assign(readFields(), { photo: state.photo }));
      var result = paginate(recipe, opts());
      last = result;
      preview.innerHTML = result.cards
        .map(function (c, i) {
          return '<div class="rct-sheet" role="img" aria-label="Card ' + (i + 1) + ' of ' + result.cards.length + '">' + c + '</div>';
        })
        .join('');
      fitSheets();

      var n = result.cards.length;
      var msg = '';
      if (state.blank) msg = 'Blank card. Print it and write on it.';
      else if (n > 1) msg = 'This recipe runs to ' + n + ' cards. Print them back to back, or pick a bigger size.';
      if (result.clipped) msg = 'Some text is too long to fit. Try a bigger size or shorten the longest line.';
      statusEl.textContent = msg;
      statusEl.hidden = !msg;
    }

    var timer;
    function schedule() {
      clearTimeout(timer);
      timer = setTimeout(function () {
        render();
        save();
      }, 120);
    }

    // Design picker: a radio group of buttons.
    var designs = root.querySelector('[data-rct-designs]');
    designs.addEventListener('click', function (e) {
      var b = e.target.closest('[data-rct-design]');
      if (!b) return;
      state.design = b.dataset.rctDesign;
      syncControls();
      schedule();
    });
    designs.addEventListener('keydown', function (e) {
      var keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      if (!keys[e.key]) return;
      e.preventDefault();
      var ids = DESIGNS.map(function (d) { return d.id; });
      var i = (ids.indexOf(state.design) + keys[e.key] + ids.length) % ids.length;
      state.design = ids[i];
      syncControls();
      root.querySelector('[data-rct-design="' + ids[i] + '"]').focus();
      schedule();
    });

    root.addEventListener('change', function (e) {
      var t = e.target;
      if (t.name === 'rct-size') state.size = t.value;
      else if (t.name === 'rct-mode') state.blank = t.value === 'blank';
      else if (t.matches('[data-rct-paper]')) state.paper = t.value;
      else if (t.matches('[data-rct-credit]')) state.credit = t.checked;
      else return;
      syncControls();
      schedule();
    });

    root.querySelectorAll('[data-rct-swatch]').forEach(function (b) {
      b.addEventListener('click', function () {
        state.accent = b.dataset.rctSwatch;
        syncControls();
        schedule();
      });
    });
    var colorInput = root.querySelector('[data-rct-color]');
    if (colorInput) {
      colorInput.addEventListener('input', function () {
        state.accent = colorInput.value;
        syncControls();
        schedule();
      });
    }

    form.addEventListener('input', schedule);
    form.addEventListener('submit', function (e) {
      e.preventDefault();
    });

    photoInput.addEventListener('change', function () {
      var file = photoInput.files && photoInput.files[0];
      if (!file) return;
      if (!/^image\//.test(file.type)) return;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = URL.createObjectURL(file);
      state.photo = objectUrl;
      state.photoIsExample = false;
      syncControls();
      schedule();
      track('recipe-card-photo');
    });
    photoClear.addEventListener('click', function () {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = null;
      state.photo = '';
      photoInput.value = '';
      syncControls();
      schedule();
    });

    root.querySelector('[data-rct-example-load]').addEventListener('click', function () {
      setFields(example);
      state.photo = defaultPhoto;
      state.photoIsExample = true;
      state.blank = false;
      syncControls();
      schedule();
    });
    root.querySelector('[data-rct-clear]').addEventListener('click', function () {
      setFields({});
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = null;
      state.photo = '';
      photoInput.value = '';
      syncControls();
      schedule();
      field('title').focus();
    });

    // Print: a clean copy of the cards as a direct child of <body>, and a page size to match.
    root.querySelector('[data-rct-print]').addEventListener('click', function () {
      if (!last) render();
      var layout = printPages(last.cards, state.size, state.paper);
      var old = document.getElementById('rct-print-root');
      if (old) old.remove();
      var print = document.createElement('div');
      print.id = 'rct-print-root';
      print.innerHTML = layout.pages.join('');
      document.body.appendChild(print);
      var pageStyle = document.getElementById('rct-page-style');
      if (!pageStyle) {
        pageStyle = document.createElement('style');
        pageStyle.id = 'rct-page-style';
        document.head.appendChild(pageStyle);
      }
      pageStyle.textContent = '@page { size: ' + layout.pageSize + '; margin: 0; }';
      document.documentElement.classList.add('rct-printing');
      track('recipe-card-print', {
        design: state.design,
        size: state.size,
        paper: SIZES[state.size].card ? state.paper : 'page',
        blank: state.blank,
        cards: last.cards.length,
      });
      var imgs = print.querySelectorAll('img');
      var waits = Array.prototype.map.call(imgs, function (img) {
        return img.complete ? Promise.resolve() : new Promise(function (res) { img.onload = img.onerror = res; });
      });
      Promise.all(waits).then(function () {
        window.print();
      });
    });
    window.addEventListener('afterprint', function () {
      document.documentElement.classList.remove('rct-printing');
      var p = document.getElementById('rct-print-root');
      if (p) p.remove();
    });

    // Design thumbnails: the example recipe in each design, drawn once.
    var exampleRecipe = parse(Object.assign({}, example, { photo: defaultPhoto }));
    root.querySelectorAll('[data-rct-design]').forEach(function (b) {
      var thumb = b.querySelector('[data-rct-thumb]');
      if (!thumb) return;
      thumb.innerHTML = cardHTML(exampleRecipe, { design: b.dataset.rctDesign, size: '4x6', accent: '#eb4c2e', credit: false });
    });

    syncControls();
    render();
    window.addEventListener('resize', fitSheets);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(render);
  }

  function boot() {
    document.querySelectorAll('[data-rct]').forEach(initTool);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
